import { WORLDS } from '../../content/campaign';
import {
  hueDeg,
  RESERVED_HUE_MAX,
  RESERVED_HUE_MIN,
  RESERVED_SAT_LIMIT,
  satHsv,
} from '../palette';

/* ═══════════════════════════════════════════════════════════════════════════
   LES LUT PAR MONDE — l'étalonnage, et son garde-fou intégré (PLAN.md §4.3).

   Trois mondes, trois ambiances chromatiques. Une LUT 3D est le bon outil :
   c'est le seul moyen de repeindre TOUT l'écran d'un coup — décor, coques,
   explosions, interface 3D — sans toucher à un seul matériau, donc sans
   recompiler un seul shader.

   ── Où elle se place, et pourquoi c'est la seule position possible ────────

     Bloom → aberration chromatique → **ACES** → **LUT** → vignette
                                                └── ici, et pas ailleurs

   AVANT ACES, la LUT étalonnerait des valeurs HDR non bornées : une table
   indexée sur [0,1] ne peut rien dire d'une luminance de 6,0, et le résultat
   dépendrait de l'intensité du bloom. APRÈS ACES, l'image est display-referred,
   bornée, et l'étalonnage veut exactement dire ce qu'il dit. La vignette passe
   ensuite parce qu'elle n'est qu'un assombrissement : l'étalonner reviendrait à
   étalonner un cache.

   ⚠️ Et surtout : la LUT vit DANS le composer, donc la scène OVERLAY ne la
   traverse jamais. Les projectiles ennemis gardent la couleur exacte qui leur a
   été autorisée, dans les trois mondes, quoi qu'on fasse ici. C'est le troisième
   bénéfice de la couche danger annoncé par §4.4, et il n'est acquis que tant que
   personne ne déplace la LUT hors du composer — ce qu'un test vérifie.

   ── Le garde-fou ─────────────────────────────────────────────────────────

   Un étalonnage chaud est, par nature, une machine à fabriquer du rouge. Or le
   rouge, le rose et le violet appartiennent aux projectiles ennemis et à rien
   d'autre. Plutôt que d'espérer qu'un réglage manuel reste sage, la fonction
   d'étalonnage REFUSE de produire ces teintes : au-delà d'un seuil de
   saturation dans la bande réservée, elle désature vers la luminance, avec un
   fondu de 12° pour qu'aucune discontinuité n'apparaisse dans la table.

   La contrainte devient donc structurelle, et non plus documentaire. On peut
   pousser le monde 3 aussi loin qu'on veut : il virera à l'ambre, jamais au
   sang.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface Grade {
  readonly id: string;
  readonly name: string;
  /** Remontée des noirs, par canal. Donne l'ambiance des zones sombres, qui
   *  occupent l'essentiel de l'écran d'un shmup. */
  readonly lift: readonly [number, number, number];
  /** Contraste des tons moyens, par canal. > 1 éclaircit. */
  readonly gamma: readonly [number, number, number];
  /** Gain sur les hautes lumières, par canal. */
  readonly gain: readonly [number, number, number];
  /** 1 = neutre, < 1 désature. */
  readonly saturation: number;
}

/* ── Le calibrage du garde-fou ─────────────────────────────────────────────
   Trois nombres, et chacun est contraint des deux côtés.

   `GUARD_FEATHER` — la largeur du fondu. Sans fondu, la table présenterait une
   discontinuité de teinte qu'une interpolation trilinéaire transformerait en
   frange colorée visible sur les dégradés du décor.

   `GUARD_MARGIN` — de combien le clamp atteint sa pleine puissance AVANT
   d'entrer dans la bande interdite. ⚠️ Sans cette marge, la table est correcte
   en flottant et fausse une fois quantifiée sur 8 bits : un texel de teinte
   260,6° — donc légalement hors bande — s'arrondit à 262,0° et bascule dedans.
   Quatre degrés d'avance suffisent, et le symptôme sans eux est une poignée de
   texels rouges sur 4 096, c'est-à-dire invisible à l'œil et rouge en CI.

   ⚠️ La somme des deux borne le côté chaud : `RESERVED_HUE_MAX + MARGIN +
   FEATHER` doit rester SOUS l'orange des explosions (28,6°), sinon le garde-fou
   censé protéger la couche danger désaturerait le seul effet que le jeu a le
   droit de rendre spectaculaire. 14 + 4 + 8 = 26, et c'est pour cela que le
   fondu vaut 8 et non 12. */
const GUARD_FEATHER = 8;
const GUARD_MARGIN = 4;
const GUARD_MIN = RESERVED_HUE_MIN - GUARD_MARGIN - GUARD_FEATHER;
const GUARD_MAX = RESERVED_HUE_MAX + GUARD_MARGIN + GUARD_FEATHER;
/** Marge sous le seuil de saturation : une égalité stricte serait à la merci
 *  d'un arrondi sur 8 bits. */
const GUARD_TARGET = RESERVED_SAT_LIMIT * 0.9;
/** Le garde-fou s'applique un peu plus bas que le seuil de valeur du test, pour
 *  la même raison : une couleur à 0,249 s'arrondit à 0,251. */
const GUARD_MIN_VALUE = 0.22;

/** Profondeur dans la bande réservée : 0 hors de portée, 1 en plein dedans. */
function bandDepth(h: number): number {
  const x = h < GUARD_MAX ? h + 360 : h;
  const lo = GUARD_MIN;
  const hi = GUARD_MAX + 360;
  if (x < lo || x > hi) return 0;
  const d = Math.min(x - lo, hi - x);
  return Math.min(1, d / GUARD_FEATHER);
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Luminance Rec. 709. C'est autour d'elle qu'on désature, pour qu'une couleur
 *  corrigée ne change jamais de valeur — seulement de teinte. */
function lum(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Étalonne une couleur en espace d'affichage. Le résultat est écrit dans `out`
 * — aucune allocation, la fonction tourne 4 096 fois par reconstruction de LUT.
 */
export function applyGrade(
  g: Grade,
  r0: number,
  g0: number,
  b0: number,
  out: [number, number, number],
): void {
  let r = r0, gg = g0, b = b0;

  // Lift / gamma / gain, dans cet ordre — c'est la convention de tout étalonneur
  // et l'inverser rendrait les réglages illisibles pour qui en a déjà fait.
  r = r + g.lift[0] * (1 - r);
  gg = gg + g.lift[1] * (1 - gg);
  b = b + g.lift[2] * (1 - b);

  r = Math.pow(clamp01(r), 1 / g.gamma[0]);
  gg = Math.pow(clamp01(gg), 1 / g.gamma[1]);
  b = Math.pow(clamp01(b), 1 / g.gamma[2]);

  r = clamp01(r * g.gain[0]);
  gg = clamp01(gg * g.gain[1]);
  b = clamp01(b * g.gain[2]);

  const l = lum(r, gg, b);
  r = clamp01(l + (r - l) * g.saturation);
  gg = clamp01(l + (gg - l) * g.saturation);
  b = clamp01(l + (b - l) * g.saturation);

  /* ── Garde-fou de la palette danger ───────────────────────────────────────
     On ramène la couleur vers sa luminance jusqu'à ce que sa saturation TSV
     redescende sous le seuil autorisé.

     ⚠️ Le facteur ne se déduit PAS d'une règle de trois. Rapprocher les canaux
     de la luminance d'un facteur k divise l'écart max−min par k, mais divise
     aussi le maximum — et la saturation TSV est le rapport des deux. Un facteur
     `cible / saturation` laisse en pratique plus du double de la saturation
     visée, ce qui ne se voit sur aucun réglage isolé mais laisse passer un
     millier de texels rouges dans la table. Il faut résoudre :

         s(k) = k·(max−min) / (l + (max−l)·k) = cible
      ⇒  k    = cible·l / [ (max−min) − cible·(max−l) ]

     Un dénominateur négatif ou nul signifie que la saturation ne peut pas
     atteindre la cible en désaturant : c'est le cas quand la couleur est déjà
     sous le seuil, et il n'y a rien à faire. */
  const s = satHsv(r, gg, b);
  const v = Math.max(r, gg, b);
  if (s > GUARD_TARGET && v >= GUARD_MIN_VALUE) {
    const depth = bandDepth(hueDeg(r, gg, b));
    if (depth > 0) {
      const target = s + (GUARD_TARGET - s) * depth;
      const mn = Math.min(r, gg, b);
      const l2 = lum(r, gg, b);
      const denom = v - mn - target * (v - l2);
      if (denom > 1e-6) {
        const k = Math.min(1, Math.max(0, (target * l2) / denom));
        // k ≤ 1 et l2 ≥ 0 garantissent que rien ne sort de [0,1] : un clamp
        // ici réintroduirait précisément la saturation qu'on vient de retirer.
        r = l2 + (r - l2) * k;
        gg = l2 + (gg - l2) * k;
        b = l2 + (b - l2) * k;
      }
    }
  }

  out[0] = r;
  out[1] = gg;
  out[2] = b;
}

/** Mélange linéaire de deux étalonnages. Sert aux transitions de monde : on
 *  interpole les PARAMÈTRES, pas les tables, ce qui reste cohérent même quand
 *  le garde-fou entre en action au milieu du fondu. */
export function blendGrade(a: Grade, b: Grade, t: number): Grade {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const mix = (x: number, y: number): number => x + (y - x) * k;
  return {
    id: `${a.id}→${b.id}`,
    name: `${a.name} → ${b.name}`,
    lift: [mix(a.lift[0], b.lift[0]), mix(a.lift[1], b.lift[1]), mix(a.lift[2], b.lift[2])],
    gamma: [mix(a.gamma[0], b.gamma[0]), mix(a.gamma[1], b.gamma[1]), mix(a.gamma[2], b.gamma[2])],
    gain: [mix(a.gain[0], b.gain[0]), mix(a.gain[1], b.gain[1]), mix(a.gain[2], b.gain[2])],
    saturation: mix(a.saturation, b.saturation),
  };
}

/** Côté de la table 3D.
 *
 *  16 et pas 32 : une LUT stylistique n'a aucun détail fin à porter, 16³ tient
 *  en 16 Ko, et la reconstruction complète pendant un fondu de monde coûte
 *  4 096 évaluations — de l'ordre de quelques dixièmes de milliseconde, sur des
 *  frames où le jeu est de toute façon en pause d'écran de cartes. En 32³ le
 *  même fondu coûterait huit fois plus, pour une différence invisible. */
export const LUT_SIZE = 16;

/* ── COMPENSATION DU DALTONISME ─────────────────────────────────────────────
   Dans ce jeu la COULEUR porte le danger : rouge/rose/violet = « ça tue »,
   orange = décor et explosions. Un joueur deutan/protan confond précisément ces
   deux familles (voir `palette.ts`), et perd alors l'information vitale.

   Ces modes DALTONISENT la LUT finale (donc TOUT le rendu du jeu, d'un coup, sans
   toucher au reste) : on simule la déficience, on mesure l'erreur — ce que l'œil
   ne distingue plus — et on la réinjecte dans les canaux encore perçus. Le rouge
   du danger s'écarte alors visiblement de l'orange. Purement visuel, aucune
   incidence sur la simulation ni sur l'empreinte de replay. */
export const Colorblind = {
  None: 0,
  Protanopie: 1,
  Deuteranopie: 2,
  Tritanopie: 3,
  /** Deutéranopie (la plus fréquente) à correction RENFORCÉE, pour qui en a
   *  besoin plus fortement. */
  Renforce: 4,
} as const;

export type ColorblindMode = (typeof Colorblind)[keyof typeof Colorblind];

/* Matrices de SIMULATION de dichromatie en RGB (approximation sRGB usuelle,
   type Vischeck/Fidaner). Row-major, 3×3 aplaties : chaque triplet est une
   composante de sortie. Suffisantes pour une aide de jeu — on ne prétend pas à
   l'exactitude clinique, seulement à rendre le danger de nouveau distinguable. */
const CVD_SIM: Readonly<Record<number, readonly number[]>> = {
  [Colorblind.Protanopie]: [0.567, 0.433, 0, 0.558, 0.442, 0, 0, 0.242, 0.758],
  [Colorblind.Deuteranopie]: [0.625, 0.375, 0, 0.7, 0.3, 0, 0, 0.3, 0.7],
  [Colorblind.Tritanopie]: [0.95, 0.05, 0, 0, 0.433, 0.567, 0, 0.475, 0.525],
  [Colorblind.Renforce]: [0.625, 0.375, 0, 0.7, 0.3, 0, 0, 0.3, 0.7],
};

/** Daltonise une couleur RGB (composantes [0,1]) EN PLACE. No-op pour `None` ou
 *  un mode inconnu — le tir de série et toutes les LUT existantes passent alors
 *  par un chemin qui ne touche à rien. */
export function daltonize(rgb: [number, number, number], mode: number): void {
  const m = CVD_SIM[mode];
  if (!m) return;
  const r = rgb[0]!;
  const g = rgb[1]!;
  const b = rgb[2]!;
  // 1. Ce que l'œil déficient PERÇOIT.
  const sr = m[0]! * r + m[1]! * g + m[2]! * b;
  const sg = m[3]! * r + m[4]! * g + m[5]! * b;
  const sb = m[6]! * r + m[7]! * g + m[8]! * b;
  // 2. L'ERREUR : ce que la déficience efface.
  const er = r - sr;
  const eg = g - sg;
  const eb = b - sb;
  // 3. Réinjection de l'erreur rouge-vert dans les canaux encore distingués
  //    (la matrice de décalage classique de la daltonisation), renforcée pour le
  //    mode dédié.
  const k = mode === Colorblind.Renforce ? 1.5 : 1.0;
  const cg = g + k * (0.7 * er + eg);
  const cb = b + k * (0.7 * er + eb);
  rgb[1] = cg < 0 ? 0 : cg > 1 ? 1 : cg;
  rgb[2] = cb < 0 ? 0 : cb > 1 ? 1 : cb;
}

/**
 * Écrit la table dans un tampon RGBA 8 bits déjà alloué. `colorblind` daltonise
 * chaque cellule après l'étalonnage de monde (0 = aucun, chemin inchangé).
 *
 * ⚠️ L'ordre des axes est (r → x, g → y, b → z), c'est-à-dire l'ordre
 * d'indexation d'une `Data3DTexture`. L'intervertir produit une image
 * fausse mais plausible — des couleurs qui « ne vont pas », sans qu'aucune soit
 * assez aberrante pour désigner la cause.
 */
export function writeLut(out: Uint8Array, grade: Grade, colorblind = 0, size = LUT_SIZE): void {
  const rgb: [number, number, number] = [0, 0, 0];
  const d = size - 1;
  let o = 0;
  for (let z = 0; z < size; z++) {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        applyGrade(grade, x / d, y / d, z / d, rgb);
        if (colorblind !== 0) daltonize(rgb, colorblind);
        out[o++] = Math.round(rgb[0] * 255);
        out[o++] = Math.round(rgb[1] * 255);
        out[o++] = Math.round(rgb[2] * 255);
        out[o++] = 255;
      }
    }
  }
}

/* ── Les étalonnages de monde ──────────────────────────────────────────────
   Ils ne diffèrent pas seulement en teinte : ils diffèrent en CONTRASTE et en
   saturation, parce qu'une ambiance qu'on ne distingue qu'à la teinte disparaît
   pour 8 % des joueurs.

   Les TROIS PREMIERS sont ceux de la campagne actuelle (Fer/Cendre/Forge, monde
   1/2/3). Les suivants sont une réserve pour l'extension à dix mondes : chacun
   distinct en teinte ET en saturation, tous dans les bornes que les tests de
   §4.5 imposent (le garde-fou danger reste STRUCTUREL, les explosions gardent
   leur orange, le décor reste sous son plafond de luminance, noir et blanc
   tiennent). Un monde résout son étalonnage par `gradeId` (table d'identité) ou,
   à défaut, par son rang. */

export const GRADES: readonly Grade[] = [
  {
    // W1 — FER. Bleu acier, propre, contrasté. C'est le monde qui enseigne :
    // l'étalonnage y est le plus neutre possible, pour que rien n'ajoute de
    // difficulté de lecture pendant qu'on apprend le vocabulaire.
    id: 'fer',
    name: 'Fer',
    lift: [0.0, 0.008, 0.03],
    gamma: [1.0, 1.02, 1.06],
    gain: [0.94, 0.99, 1.1],
    saturation: 0.98,
  },
  {
    // W2 — CENDRE. Désaturé, verdâtre, plus sombre. Le monde de la TISSERANDE
    // est celui de la lecture de formes ; on baisse donc la saturation générale
    // pour que les rosaces de projectiles soient la seule chose colorée.
    id: 'cendre',
    name: 'Cendre',
    lift: [0.012, 0.02, 0.012],
    gamma: [0.96, 1.0, 0.95],
    gain: [0.95, 1.02, 0.92],
    saturation: 0.76,
  },
  {
    // W3 — FORGE. Chaud, ambré, ombres brunes. C'est le seul étalonnage qui
    // pousse réellement le garde-fou : sans lui, ce réglage produirait des
    // rouges dans les hautes lumières, et le joueur verrait des projectiles
    // partout dans le décor de LA FORGE.
    id: 'forge',
    name: 'Forge',
    lift: [0.045, 0.022, 0.004],
    gamma: [1.05, 0.98, 0.9],
    gain: [1.12, 0.99, 0.8],
    saturation: 1.04,
  },
  {
    // GLACE — bleu froid, contrasté, légèrement désaturé. Un monde de gel.
    id: 'glace',
    name: 'Glace',
    lift: [0.0, 0.01, 0.028],
    gamma: [0.98, 1.0, 1.05],
    gain: [0.9, 0.98, 1.12],
    saturation: 0.88,
  },
  {
    // ÉMERAUDE — vert dominant et saturé, par contraste (rouge/bleu en retrait).
    id: 'emeraude',
    name: 'Émeraude',
    lift: [0.004, 0.02, 0.006],
    gamma: [0.97, 1.04, 0.96],
    gain: [0.86, 1.02, 0.9],
    saturation: 1.06,
  },
  {
    // AMBRE — jaune-or chaud et lumineux, plus clair que la Forge.
    id: 'ambre',
    name: 'Ambre',
    lift: [0.03, 0.024, 0.006],
    gamma: [1.03, 1.0, 0.92],
    gain: [1.1, 1.02, 0.82],
    saturation: 1.0,
  },
  {
    // ABYSSE — bleu très sombre, ombres profondes, désaturé. Le fond du secteur.
    id: 'abysse',
    name: 'Abysse',
    lift: [0.004, 0.008, 0.02],
    gamma: [0.92, 0.95, 1.0],
    gain: [0.82, 0.9, 1.06],
    saturation: 0.72,
  },
  {
    // CUIVRE — orange cuivré, mat et terreux, distinct de la Forge (plus sourd).
    id: 'cuivre',
    name: 'Cuivre',
    lift: [0.04, 0.02, 0.01],
    gamma: [1.02, 0.97, 0.93],
    gain: [1.08, 0.95, 0.85],
    saturation: 0.9,
  },
  {
    // JADE — bleu-vert calme, désaturé. Une station en veille.
    id: 'jade',
    name: 'Jade',
    lift: [0.006, 0.016, 0.016],
    gamma: [0.96, 1.0, 1.0],
    gain: [0.9, 1.0, 1.0],
    saturation: 0.8,
  },
  {
    // SPECTRE — quasi monochrome, sourd, inquiétant. Le vide entre deux mondes.
    id: 'spectre',
    name: 'Spectre',
    lift: [0.012, 0.012, 0.014],
    gamma: [0.98, 0.98, 0.99],
    gain: [0.94, 0.95, 0.98],
    saturation: 0.7,
  },
];

/** Étalonnage à un INDEX de grade (0-based), borné aux extrémités : un index
 *  hors bornes rend un grade valide, jamais `undefined`. */
export function gradeAt(index: number): Grade {
  return GRADES[Math.max(0, Math.min(index, GRADES.length - 1))] ?? GRADES[0]!;
}

/** Index d'un grade par son identifiant, ou -1 s'il n'existe pas. */
export function gradeIndexById(id: string): number {
  return GRADES.findIndex((g) => g.id === id);
}

/** Étalonnage d'un monde, à partir de son rang dans la campagne (1-based).
 *
 *  Résout d'ABORD le `gradeId` déclaré sur le monde (table d'identité de
 *  `WorldDef`) : c'est ce qui découple l'ambiance visuelle de la position dans la
 *  campagne — deux mondes peuvent partager une palette, et l'ordre des mondes
 *  peut changer sans redistribuer les couleurs. À DÉFAUT (monde sans `gradeId`,
 *  comportement historique), l'index par rang. Un rang inconnu retombe sur un
 *  grade borné : un monde ajouté sans étalonnage doit s'afficher, pas disparaître. */
export function gradeForWorld(worldNo: number): Grade {
  const world = WORLDS[worldNo - 1];
  if (world?.gradeId) {
    const i = gradeIndexById(world.gradeId);
    if (i >= 0) return gradeAt(i);
  }
  return gradeAt(worldNo - 1);
}
